import React, { useState, useEffect, useMemo, useContext } from 'react';
import { Text, ScrollView, View, TouchableOpacity, Appearance } from 'react-native';
import { useUnistyles } from 'react-native-unistyles';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { saveUserSettings } from '@/data/settings';
import { announcementPreferences } from '@/data/announcements';
import { FORM_ENTRY_NOTIFICATION, formEntryPreferences, toggleFormEntryPreference } from '@/utils/formEntryNotifications';
import { clearFileCache } from '@/utils/FileManager';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import SegmentedTabs from '@/components/General/SegmentedTabs';
import NotificationSettingGroup from '@/components/General/inputs/NotificationSettingGroup';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as LocalAuthentication from 'expo-local-authentication';
import useAuthStore from '@/data/store/useAuthStore';
import usePlatform from '@/utils/hooks/usePlatform';
import { supabase } from '@/data/client';
import Toast from 'react-native-toast-message';
import * as Application from 'expo-application';
import { showAlert } from '@/utils/showAlert';
import { useRouter } from 'expo-router';
import Toggle from '@/components/General/inputs/Toggle';
import { StyleSheet } from 'react-native-unistyles'
import Unicon from '@/components/General/Unicon';
import { PATCH_VERSION } from '@/config/version';
import TabConfigModal from '@/components/General/TabConfigModal';
import { GuideContext, GuideProgressContext } from '@/components/Guides/GuideProvider';
import { GUIDES_ENABLED } from '@/config/guides';
import MilestonePreferences from '@/components/Milestones/MilestonePreferences';
import { MILESTONES_ENABLED } from '@/config/milestones';

export default function SettingsScreen() {
    /*** State ***/
    const [ currentSettings, setCurrentSettings ] = useState({})
    const [ deviceSettings, setDeviceSettings ] = useState({})
    const [ tabConfigOpen, setTabConfigOpen ] = useState(false)

    const setTheme = useAuthStore(state => state.setTheme);
    const theme = useAuthStore(state => state.theme);

    /*** Theme & Dimensions ***/
    const {theme: {colors}} = useUnistyles();
    const { isNative } = usePlatform();
    const router = useRouter();


    /*** Queries ***/
    const { data: loggedInUser } = Query.people.loggedInUser()
    const { data: notificationTypes } = Query.notificationTypes.all()
    const { data: hasEditableForms } = Query.forms.hasEditable({ personID: loggedInUser?.id, organizationID: loggedInUser?.organizationID })
    const queryClient = useQueryClient();
    const saveUserSettingsMutation = useMutation({
        mutationFn: saveUserSettings,
        onSuccess: (_data, variables) => {
            // RegisteredUsers writes don't invalidate the persisted loggedInUser
            // cache (15min staleTime) — this screen reads userSettings from it on
            // mount, so sync it here or controls show stale values on revisit.
            queryClient.setQueryData(QueryKeys.people.loggedInUser(), previous => previous ? {
                ...previous,
                userSettings: {
                    ...(previous.userSettings || {}),
                    ...variables,
                    theme: variables?.theme ? variables.theme.toLowerCase() : previous.userSettings?.theme,
                },
            } : previous);
        },
    });

    const guide = useContext(GuideContext);
    const guideProgress = useContext(GuideProgressContext);
    const showGuidePreference = GUIDES_ENABLED && !!guide?.accountID;

    /*** Effects ***/
    useEffect(() => { 
        if(!isNative) document.title = `User Settings | Reach`
        setCurrentSettings(loggedInUser?.userSettings || {});
    }, [loggedInUser])


    useEffect(() => {
        getDeviceSettings();
    }, [currentSettings]);


    const applyDeviceSetting = async (name, value) => {
        setDeviceSettings({ ...deviceSettings, [name]: value || null });

        if(!value || value === 'false'){
            await AsyncStorage.removeItem(name);
        }
        else{
            if(name == '@enableBiometrics'){
                const auth = await LocalAuthentication.authenticateAsync()

                if(auth.success){
                    value = 'true';
                }
                else{
                    value = 'false';
                }  
            }
            
            await AsyncStorage.setItem(name, value);
            
        }

        setDeviceSettings({ ...deviceSettings, [name]: value || null });
    }


    const getDeviceSettings = async () => {
        const values = await AsyncStorage.multiGet(['@enableBiometrics', '@stayLoggedIn']);
        setDeviceSettings(Object.fromEntries(values))
    }

    const updateNotificationSetting = (name, selectedItem) => {
        const current = name === FORM_ENTRY_NOTIFICATION
            ? toggleFormEntryPreference(currentSettings.notificationSettings, selectedItem)
            : [...(name === 'ORGANIZATION_ANNOUNCEMENT' ? announcementPreferences(currentSettings.notificationSettings) : currentSettings.notificationSettings?.[name] || [])];
        if (name !== FORM_ENTRY_NOTIFICATION) current.includes(selectedItem) ? current.splice(current.indexOf(selectedItem), 1) : current.push(selectedItem);
        const value = current.length === 0 && !['ORGANIZATION_ANNOUNCEMENT', FORM_ENTRY_NOTIFICATION].includes(name) ? undefined : current;
        
        setCurrentSettings({
            ...currentSettings, 
            notificationSettings: {
                ...(currentSettings.notificationSettings || {}), 
                [name]: value
            }
        })

        saveUserSettingsMutation.mutate({
            notificationSettings: {
                ...(currentSettings.notificationSettings || {}), 
                [name]: value
            }
        })
    }


    const deleteAccount = async () => {
        const userRequest = await supabase.auth.getUser();

        if(!userRequest.data?.user?.email){
            Toast.show({type: 'error', text2: "Hmm... Looks like we can't delete your account right now. Try again in a bit."})
        }

        const { error } = await supabase.auth.signInWithOtp({ email: userRequest.data?.user?.email })

        if(error){
            Toast.show({type: 'error', text2: "Hmm... Looks like we can't delete your account right now. Try again in a bit."})
        }
        else{
            router.push({pathname: "/settings/delete-account", params: {recoveryEmail: userRequest.data?.user?.email}})
        }
    }


    const notificationTypeInputs = useMemo(() => {
        const filteredTypes = notificationTypes?.filter(type => {
            if (type?.id === FORM_ENTRY_NOTIFICATION && hasEditableForms !== true) return false;
            if(type?.module == "admin" && !loggedInUser?.isAdmin){
                return false;
            }
            if(type?.module == "giving" && !loggedInUser?.can?.('View Contributions')){
                return false;
            }
            if(type?.module == "registrations" && !loggedInUser?.isAdmin){
                return false;
            }
            return true;
        }) || [];

        return filteredTypes.filter(type => type?.name !== "Organization join request was accepted").map((type) => (
            <NotificationSettingGroup
                key={type?.id}
                testID={`notification-setting-${type?.id}`}
                title={type?.name}
                description={type?.id === 'ORGANIZATION_ANNOUNCEMENT' ? 'Prefer push for organization announcements. When push is off or unavailable, opted-in SMS or eligible email is used instead. The Board and in-app notification are always included.' : type?.description}
                options={type?.id === 'ORGANIZATION_ANNOUNCEMENT' ? ['Push'] : ["Push", "Email"]}
                selectedValues={type?.id === FORM_ENTRY_NOTIFICATION ? formEntryPreferences(currentSettings?.notificationSettings) : type?.id === 'ORGANIZATION_ANNOUNCEMENT' ? announcementPreferences(currentSettings?.notificationSettings) : currentSettings?.notificationSettings?.[type?.id] || []}
                onOptionChange={(selectedItem) => {
                    updateNotificationSetting(type?.id, selectedItem)
                }}
            />
        ));
    }, [notificationTypes, currentSettings?.notificationSettings, loggedInUser, hasEditableForms, theme])

    const SettingRow = ({ icon, label, description, onPress, right, isFirst, isLast, destructive }) => (
        <TouchableOpacity
            activeOpacity={onPress ? 0.6 : 1}
            onPress={onPress}
            style={[
                styles.row,
                isFirst && styles.rowFirst,
                isLast && styles.rowLast,
                !isLast && styles.rowBorder,
            ]}
        >
            <View style={styles.rowLeft}>
                <View style={[styles.iconCircle, destructive && styles.iconCircleDestructive]}>
                    <Unicon name={icon} size={18} colorVariant={destructive ? "error" : "primary"} />
                </View>
                <View style={styles.rowTextContainer}>
                    <Text style={[styles.rowLabel, destructive && {color: colors.error}]}>{label}</Text>
                    {description ? <Text style={styles.rowDescription}>{description}</Text> : null}
                </View>
            </View>
            {right || (onPress && <Unicon name="chevron-forward" size={18} colorVariant="textTertiary" />)}
        </TouchableOpacity>
    );

    return (
        <View style={{flex: 1}}>
            <ScrollView showsVerticalScrollIndicator={false} style={{flex: 1}} contentContainerStyle={styles.scrollContent} keyboardShouldPersistTaps="handled"
>
                <View style={styles.container}>

                    <View style={styles.section}>
                        <Text style={styles.sectionTitle}>Guides & Help</Text>
                        <View style={styles.card}>
                            {showGuidePreference && (
                                <SettingRow
                                    icon="sparkles-outline"
                                    label="Show first-visit guides"
                                    description={guideProgress?.isError ? "Guide preferences are temporarily unavailable." : "Automatically explain features you haven't viewed or skipped. Manual replay stays available."}
                                    isFirst
                                    right={
                                        <Toggle
                                            testID="guides-auto-setting"
                                            accessibilityLabel="Show first-visit guides"
                                            value={guideProgress?.state?.autoGuidesEnabled !== false}
                                            disabled={!guideProgress?.state}
                                            onValueChange={(value) => guide.toggleAutomatic(value)}
                                        />
                                    }
                                />
                            )}
                            <SettingRow icon="help-circle-outline" label="Browse guides" description="Replay walkthroughs or open the Knowledge Base" onPress={() => router.push('/guides')} isFirst={!showGuidePreference} isLast />
                        </View>
                    </View>

                    {MILESTONES_ENABLED && (
                        <View style={styles.section}>
                            <Text style={styles.sectionTitle}>Milestones</Text>
                            <View style={styles.card}>
                                <MilestonePreferences />
                            </View>
                        </View>
                    )}

                    <View style={styles.section}>
                        <Text style={styles.sectionTitle}>Appearance</Text>
                        <View style={styles.card}>
                            <View style={styles.cardInner}>
                                <Text style={styles.themeLabel}>Theme</Text>
                                <SegmentedTabs
                                    testIDPrefix="theme-option"
                                    tabs={[
                                        { key: 'default', label: 'Default' },
                                        { key: 'light', label: 'Light' },
                                        { key: 'dark', label: 'Dark' },
                                    ]}
                                    value={(currentSettings?.theme || 'default').toLowerCase()}
                                    onChange={(key) => {
                                        setTheme(key);

                                        if (isNative) {
                                            Appearance.setColorScheme(key === 'default' ? null : key);
                                        }

                                        setCurrentSettings({ ...currentSettings, theme: key });

                                        saveUserSettingsMutation.mutate({ theme: key });
                                    }}
                                />
                                <Text style={styles.helpText}>
                                    The default will use your device&apos;s theme. You can override the theme here.
                                </Text>
                            </View>
                        </View>
                    </View>

                    <View style={styles.section}>
                        <Text style={styles.sectionTitle}>Notifications</Text>
                        <Text style={styles.sectionSubtitle}>
                            We may still send you important notifications about your account outside of your notification settings.
                        </Text>
                        <View style={styles.card}>
                            <View style={styles.notificationInner}>
                                {notificationTypeInputs}
                            </View>
                        </View>
                    </View>

                    {/* <View style={styles.section}>
                        <Text style={styles.sectionTitle}>SMS Opt-In</Text>
                        <Text style={styles.sectionSubtitle}>
                            Receive text communications from this organization through Reach.
                        </Text>
                        <View style={styles.card}>
                            <SettingRow
                                icon="chatbubbles-outline"
                                label="Receive SMS Communications"
                                description={(() => { const smsPhone = loggedInUser?.Phones?.find(p => p.smsOptInStatus === 'opted_in') || loggedInUser?.Phones?.[0]; return smsPhone ? `Texts will be sent to ${smsPhone.phoneNumber}` : "No phone number on file. Add a phone number to your profile to enable SMS communications."; })()}
                                isFirst
                                isLast
                                right={
                                    <Toggle
                                        onValueChange={(newVal) => {
                                            const newSettings = {...currentSettings, textMessagingOptIn: newVal};
                                            setCurrentSettings(newSettings);
                                        }}
                                        value={currentSettings?.textMessagingOptIn || false}
                                        disabled={!loggedInUser?.Phones?.length}
                                    />
                                }
                            />
                        </View>
                    </View> */}

                    {isNative && (
                        <View style={styles.section}>
                            <Text style={styles.sectionTitle}>Navigation</Text>
                            <View style={styles.card}>
                                <SettingRow
                                    icon="grid-outline"
                                    label="Customize Bottom Tabs"
                                    description="Choose what appears on your bottom tab bar"
                                    onPress={() => setTabConfigOpen(true)}
                                    isFirst
                                    isLast
                                />
                            </View>
                        </View>
                    )}

                    <View style={styles.section}>
                        <Text style={styles.sectionTitle}>Account & Security</Text>
                        <View style={styles.card}>
                            {isNative &&
                                <SettingRow
                                    icon="finger-print-outline"
                                    label="Face ID"
                                    description="Unlock the app with biometrics"
                                    isFirst
                                    right={
                                        <Toggle
                                            onValueChange={(newVal) => applyDeviceSetting("@enableBiometrics", newVal.toString())}
                                            value={deviceSettings?.["@enableBiometrics"] === "true"}
                                        />
                                    }
                                />
                            }
                            <SettingRow
                                icon="phone-portrait-outline"
                                label="Manage Devices"
                                onPress={() => router.push("/settings/devices")}
                                isFirst={!isNative}
                            />
                            <SettingRow
                                icon="lock-closed-outline"
                                label="Change Password"
                                onPress={() => router.push("/auth/account/reset-password")}
                            />
                            <SettingRow
                                icon="trash-outline"
                                label="Delete Account"
                                destructive
                                isLast
                                onPress={() => {
                                    showAlert({
                                        title: "Delete Account",
                                        subtitle: "Are you sure you want to delete your account? You'll need to confim your identity to continue.",
                                        isDestructive: true,
                                        cancelText: "Nevermind",
                                        confirmText: "Continue",
                                        onConfirm: () => deleteAccount()
                                    })
                                }}
                            />
                        </View>
                    </View>

                    <View style={styles.section}>
                        <Text style={styles.sectionTitle}>Storage</Text>
                        <View style={styles.card}>
                            <SettingRow
                                icon="cloud-download-outline"
                                label="Clear File Cache"
                                description="Free up space by removing cached files"
                                onPress={() => clearFileCache()}
                                isFirst
                                isLast
                            />
                        </View>
                    </View>

                    {isNative &&
                        <View style={styles.section}>
                            <Text style={styles.sectionTitle}>About</Text>
                            <View style={styles.card}>
                                <SettingRow
                                    icon="information-circle-outline"
                                    label="Version"
                                    isFirst
                                    isLast
                                    right={
                                        <Text style={styles.versionText}>{Application.nativeApplicationVersion}.{PATCH_VERSION}</Text>
                                    }
                                />
                            </View>
                        </View>
                    }

                </View>
            </ScrollView>
            <TabConfigModal isOpen={tabConfigOpen} onClose={() => setTabConfigOpen(false)} />
        </View>
    );
}


const styles = StyleSheet.create(({colors, fonts, spacing, shadows}) => ({
    scrollContent: {
        paddingBottom: 40,
        paddingHorizontal: spacing.md,
        alignItems: "center",
    },
    container: {
        maxWidth: 600,
        width: "100%",
    },
    section: {
        marginTop: spacing.lg,
    },
    sectionTitle: {
        color: colors.textSecondary,
        fontFamily: fonts.bold,
        fontSize: 11,
        textTransform: "uppercase",
        letterSpacing: 0.8,
        marginBottom: spacing.sm,
        marginLeft: 4,
    },
    sectionSubtitle: {
        color: colors.textTertiary,
        fontFamily: fonts.body,
        fontSize: 13,
        marginBottom: spacing.sm + 4,
        marginLeft: 4,
        lineHeight: 18,
    },
    card: {
        backgroundColor: colors.surface,
        borderRadius: 12,
        overflow: "hidden",
        boxShadow: shadows.card,
    },
    cardInner: {
        padding: spacing.md,
    },
    themeLabel: {
        color: colors.textSecondary,
        fontFamily: fonts.bold,
        fontSize: 13,
        textTransform: "uppercase",
        letterSpacing: 0.5,
        marginBottom: spacing.sm,
        marginLeft: 2,
    },
    helpText: {
        color: colors.textTertiary,
        fontFamily: fonts.body,
        fontSize: 12,
        marginTop: spacing.sm,
        lineHeight: 17,
    },
    notificationInner: {
        paddingHorizontal: spacing.md + 4,
        paddingTop: spacing.xs,
        paddingBottom: spacing.md,
    },
    row: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        paddingVertical: 14,
        paddingHorizontal: spacing.md,
    },
    rowFirst: {
        borderTopLeftRadius: 12,
        borderTopRightRadius: 12,
    },
    rowLast: {
        borderBottomLeftRadius: 12,
        borderBottomRightRadius: 12,
    },
    rowBorder: {
        borderBottomWidth: 1,
        borderBottomColor: colors.border,
    },
    rowLeft: {
        flexDirection: "row",
        alignItems: "center",
        flex: 1,
        gap: 12,
    },
    iconCircle: {
        width: 34,
        height: 34,
        borderRadius: 10,
        backgroundColor: colors.primaryContainer,
        alignItems: "center",
        justifyContent: "center",
    },
    iconCircleDestructive: {
        backgroundColor: colors.errorContainer,
    },
    rowTextContainer: {
        flex: 1,
    },
    rowLabel: {
        fontFamily: fonts.bold,
        fontSize: 15,
        color: colors.text,
    },
    rowDescription: {
        fontFamily: fonts.body,
        fontSize: 12,
        color: colors.textTertiary,
        marginTop: 2,
    },
    versionText: {
        fontFamily: fonts.body,
        fontSize: 13,
        color: colors.textSecondary,
    },
    adminBanner: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        backgroundColor: colors.primary,
        borderRadius: 12,
        padding: spacing.md,
    },
    adminBannerLeft: {
        flexDirection: "row",
        alignItems: "center",
        gap: 12,
    },
    adminIconCircle: {
        width: 40,
        height: 40,
        borderRadius: 12,
        backgroundColor: "rgba(255,255,255,0.2)",
        alignItems: "center",
        justifyContent: "center",
    },
    adminBannerTitle: {
        fontFamily: fonts.bold,
        fontSize: 16,
        color: colors.onPrimary,
    },
    adminBannerSubtitle: {
        fontFamily: fonts.body,
        fontSize: 13,
        color: colors.onPrimary,
        opacity: 0.8,
        marginTop: 1,
    },
    disclaimerText: {
        color: colors.textTertiary,
        fontFamily: fonts.body,
        fontSize: 11,
        marginTop: 8,
        marginLeft: 4,
        lineHeight: 16,
    },
    disclaimerLink: {
        color: colors.primary,
        fontFamily: fonts.body,
        fontSize: 11,
        lineHeight: 16,
        textDecorationLine: 'underline',
    },
}))